// Builds DOM from site config + project manifests.
// Two top-level views:
//   - renderIndex(root, data)          → gallery of project cards
//   - renderProjectPage(root, data, p) → single project, full content
// Block types within a project: text, image, image-grid, video,
// model, model-sequence, spacer.

import { mountModelViewer, mountModelSequence } from './three-viewer.js';
import { mountFlowField } from './flow-field.js';

// Index gallery: hero + grid of project cards. Each card mounts a
// preview viewer (the project's primary model, fully assembled and
// auto-rotating) and links to its detail page.
export function renderIndex(root, { site, projects }) {
  root.innerHTML = '';
  root.appendChild(renderHeader(site, { includeNav: false }));
  root.appendChild(renderHero(site));
  root.appendChild(renderGallery(projects));
  root.appendChild(renderFooter(site));
}

// Collect photo URLs across all projects for the hero slideshow.
// Rooted Crown has both "process photo" and "portrait gallery"
// sections — only the first image-grid (the top process shots) is
// used for the landing page so the slideshow stays curated rather
// than dumping every photo on visitors. Other projects contribute
// everything.
function gatherPhotos(projects) {
  const out = [];
  for (const p of projects || []) {
    const limitToFirstGrid = p.slug === 'rooted-crown';
    let foundGrid = false;
    for (const b of p.blocks || []) {
      if (limitToFirstGrid && foundGrid) break;
      if (b.type === 'image' && b.src) {
        out.push(b.src);
      } else if (b.type === 'image-grid' && Array.isArray(b.images)) {
        for (const it of b.images) {
          out.push(typeof it === 'string' ? it : it.src);
        }
        foundGrid = true;
      }
    }
  }
  return out;
}

// Fisher-Yates shuffle — gives a fresh order on every page load.
function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Project detail page: header (with ← back), the project's blocks
// rendered in order, footer. This is where scroll-to-assemble lives.
export function renderProjectPage(root, { site }, project) {
  root.innerHTML = '';
  root.appendChild(renderHeader(site, { includeNav: false }));
  // When the project leads with a scroll-locked model-sequence, the
  // project header would just stack on top of the same info shown
  // in the model viewer (title, year, hint). Suppress it so the
  // page lands directly on the canvas.
  const leadsWithLockedSequence = (project.blocks || []).some(
    (b) => b.type === 'model-sequence' && b.scrubScroll
  );
  root.appendChild(renderProject(project, null, { hideHeader: leadsWithLockedSequence }));
  root.appendChild(renderFooter(site));

  // iOS Safari workaround: imgs below the WebGL canvas occasionally
  // stay unpainted until the user rotates the phone, which forces
  // a real viewport change. Fire a synthetic resize once the page
  // settles so the same recalc runs without user input.
  setTimeout(() => {
    window.dispatchEvent(new Event('resize'));
  }, 600);
}

function renderHeader(site, { includeNav = false, backHref = null } = {}) {
  const header = document.createElement('header');
  header.className = 'site-header';

  const brand = document.createElement('a');
  brand.href = '#/';
  brand.className = 'brand';
  brand.textContent = site.shortName || site.name || 'Studio';
  header.appendChild(brand);

  if (backHref) {
    const back = document.createElement('a');
    back.className = 'back-link';
    back.href = backHref;
    back.textContent = '← Index';
    header.appendChild(back);
  }

  return header;
}

function renderGallery(projects) {
  const wrap = document.createElement('section');
  wrap.className = 'gallery';
  // Newest first. Ties keep the order from `content/index.json`,
  // which lets you hand-pick precedence for projects from the same
  // year by reordering the list.
  const sorted = (projects || []).slice().sort((a, b) => (b.year || 0) - (a.year || 0));
  for (const p of sorted) {
    wrap.appendChild(renderCard(p));
  }
  return wrap;
}

// One card per project. Pulls the first model-y block from the
// project manifest and re-mounts it in preview mode (assembled +
// spinning). Whole card is clickable → navigates to the project.
function renderCard(project) {
  const card = document.createElement('a');
  card.className = 'project-card';
  card.href = `#/${project.slug}`;

  const viewer = document.createElement('div');
  viewer.className = 'project-card-viewer';
  card.appendChild(viewer);

  const previewBlock = findPreviewBlock(project);
  if (previewBlock) {
    // Card-specific overrides. The detail page might use a tighter
    // or looser camera padding for its own layout; for the gallery
    // we force a standard fit so every card reads at the same
    // visual scale regardless of the model's manifest config.
    const block = {
      ...previewBlock,
      previewMode: true,
      scrubScroll: false,
      hint: null,
      cameraPadding: 1.4,
    };
    // Card-only camera tilt override — lets a project ask for a more
    // top-down gallery thumbnail without changing the detail page's
    // own `cameraElevation` (e.g. a wide/flat piece that reads better
    // from slightly above in the small card view).
    if (previewBlock.previewCameraElevation != null) {
      block.cameraElevation = previewBlock.previewCameraElevation;
    }
    if (block.type === 'model-sequence') {
      // Prefer the dedicated preview bake when available — much
      // smaller payload for the gallery card.
      if (block.bakedFilePreview) block.bakedFile = block.bakedFilePreview;
      queuePreviewMount(viewer, block, 'sequence');
    } else if (block.type === 'model') {
      // Inherit the page block's motion (wobble or spin) so the
      // card preview reads the same way the detail page does.
      // Default to spinning when the manifest doesn't specify.
      if (block.wobble == null) block.autorotate = true;
      queuePreviewMount(viewer, block, 'model');
    }
  } else {
    viewer.classList.add('is-empty');
  }

  const meta = document.createElement('div');
  meta.className = 'project-card-meta';
  const title = document.createElement('h3');
  title.textContent = project.title || project.slug;
  meta.appendChild(title);
  if (project.subtitle) {
    const sub = document.createElement('p');
    sub.className = 'subtitle';
    sub.textContent = project.subtitle;
    meta.appendChild(sub);
  }
  const tag = document.createElement('p');
  tag.className = 'tag';
  tag.textContent = String(project.year || '');
  meta.appendChild(tag);

  card.appendChild(meta);
  return card;
}

function findPreviewBlock(project) {
  for (const b of project.blocks || []) {
    if (b.type === 'model-sequence' || b.type === 'model') return b;
    // For a `model-grid`, build a synthetic model block from the
    // chosen entry. Defaults to the first model, override via
    // `previewIndex` on the block.
    if (b.type === 'model-grid' && Array.isArray(b.models) && b.models.length) {
      const idx = b.previewIndex ?? 0;
      const item = b.models[idx] || b.models[0];
      const { models, cols, colsMobile, aspectRatio, previewIndex, ...defaults } = b;
      return { ...defaults, ...item, type: 'model' };
    }
  }
  return null;
}

function queuePreviewMount(container, block, kind) {
  const io = new IntersectionObserver(
    (entries, obs) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        obs.disconnect();
        const mount = kind === 'sequence' ? mountModelSequence : mountModelViewer;
        mount(container, block).catch((err) =>
          console.error('[render] preview mount failed', err)
        );
      }
    },
    { rootMargin: '300px 0px' }
  );
  io.observe(container);
}

function renderHero(site) {
  const hero = document.createElement('section');
  hero.className = 'hero';
  hero.id = 'top';

  // Animated flow-field canvas behind the headline. Lines drift
  // left→right and react to pointer/touch — feels like wind across
  // grain or current through water.
  const canvas = document.createElement('canvas');
  canvas.className = 'hero-flow';
  hero.appendChild(canvas);
  // Defer the mount until the canvas has its layout size.
  requestAnimationFrame(() => mountFlowField(canvas));

  const h1 = document.createElement('h1');
  // Build the title with explicit <br> elements rather than relying
  // on CSS `white-space: pre-line` + `\n` — iOS Safari was failing
  // to break some cached versions of the page that way.
  const titleText = site.heroTitle || site.name || '';
  const lines = titleText.split('\n');
  lines.forEach((line, i) => {
    if (i > 0) h1.appendChild(document.createElement('br'));
    h1.appendChild(document.createTextNode(line));
  });

  let subtitle = null;
  if (site.heroSubtitle) {
    subtitle = document.createElement('p');
    subtitle.className = 'hero-subtitle';
    subtitle.textContent = site.heroSubtitle;
  }

  const intro = document.createElement('p');
  intro.className = 'intro';
  intro.textContent = site.intro || '';

  const meta = document.createElement('div');
  meta.className = 'meta';
  const left = document.createElement('span');
  left.textContent = site.location || '';
  const right = document.createElement('span');
  right.className = 'scroll-cue';
  right.textContent = 'Scroll ↓';
  meta.append(left, right);

  hero.appendChild(h1);
  if (subtitle) hero.appendChild(subtitle);
  hero.append(intro, meta);
  return hero;
}

function renderProject(project, index, { hideHeader = false } = {}) {
  const sec = document.createElement('section');
  sec.className = hideHeader ? 'project flush' : 'project';
  sec.id = project.slug;
  sec.dataset.slug = project.slug;

  if (!hideHeader) {
    const head = document.createElement('div');
    head.className = 'project-header';
    const idx = document.createElement('div');
    idx.className = 'index';
    idx.textContent = index == null
      ? `${project.year || ''} ${project.kind || ''}`.trim()
      : `${String(index + 1).padStart(2, '0')} — ${project.year || ''} ${project.kind || ''}`.trim();
    const h2 = document.createElement('h2');
    h2.textContent = project.title || project.slug;
    head.appendChild(idx);
    head.appendChild(h2);
    if (project.subtitle) {
      const sub = document.createElement('div');
      sub.className = 'subtitle';
      sub.textContent = project.subtitle;
      head.appendChild(sub);
    }
    sec.appendChild(head);
  }

  for (const block of project.blocks) {
    const el = renderBlock(block);
    if (el) sec.appendChild(el);
  }

  return sec;
}

function renderBlock(b) {
  switch (b.type) {
    case 'text':       return textBlock(b);
    case 'image':      return imageBlock(b);
    case 'image-grid': return gridBlock(b);
    case 'video':      return videoBlock(b);
    case 'video-grid': return videoGridBlock(b);
    case 'media-grid': return mediaGridBlock(b);
    case 'model':      return modelBlock(b);
    case 'model-grid': return modelGridBlock(b);
    case 'model-sequence': return modelSequenceBlock(b);
    case 'downloads':  return downloadsBlock(b);
    case 'instagram':  return instagramBlock(b);
    case 'instagram-grid': return instagramGridBlock(b);
    case 'youtube':    return youtubeBlock(b);
    case 'link':       return linkBlock(b);
    case 'spacer':     return spacerBlock(b);
    default:
      console.warn('[render] unknown block type', b.type);
      return null;
  }
}

function textBlock(b) {
  const el = document.createElement('div');
  el.className = 'block block-text' + (b.dim ? ' dim' : '');
  el.textContent = b.content || '';
  return el;
}

function imageBlock(b) {
  const wrap = document.createElement('figure');
  wrap.className = `block block-image ${b.layout || 'center'}`;
  const img = document.createElement('img');
  img.src = b.src;
  img.alt = b.alt || '';
  img.loading = 'lazy';
  img.decoding = 'async';
  if (b.zoomable) makeZoomable(img);
  wrap.appendChild(img);
  if (b.caption) {
    const cap = document.createElement('figcaption');
    cap.className = 'block-caption';
    cap.textContent = b.caption;
    wrap.appendChild(cap);
  }
  return wrap;
}

function gridBlock(b) {
  const wrap = document.createElement('div');
  wrap.className = 'block block-grid';
  wrap.style.setProperty('--cols', String(b.cols || 2));
  wrap.style.setProperty('--cols-mobile', String(b.colsMobile || 1));
  // Optional override of the unified crop ratio (e.g. "3 / 2",
  // "1 / 1"). Defaults to 4 / 5 portrait via the CSS.
  // `auto` opts out of the unified crop entirely — each cell sizes
  // to its image's natural aspect, so rows align top but heights
  // vary like a brick-laid layout.
  if (b.aspectRatio === 'auto') {
    wrap.classList.add('natural-aspect');
  } else if (b.aspectRatio) {
    wrap.style.setProperty('--img-aspect', b.aspectRatio);
  }
  for (const item of b.images || []) {
    // Wrapper owns the aspect-ratio so the cell has dimensions even
    // before the image decodes — iOS Safari was failing to compute
    // sizes when aspect-ratio sat on a bare <img>, leaving the
    // tiles rendered as empty (black) boxes.
    const cell = document.createElement('div');
    cell.className = 'grid-cell';
    const img = document.createElement('img');
    const src = typeof item === 'string' ? item : item.src;
    img.alt = (typeof item === 'string' ? '' : item.alt) || '';
    img.decoding = 'async';
    img.loading = 'lazy';
    if (b.zoomable) makeZoomable(img);

    // iOS Safari sometimes never paints imgs that sit below a live
    // WebGL canvas (rooted-crown's scroll-locked sequence keeps the
    // canvas hot for the whole page life). Workaround: when each img
    // actually finishes loading, call decode() to guarantee the
    // bitmap is GPU-ready, then briefly toggle a transform on its
    // cell to force a fresh composite pass. Hooked to the load event
    // (not an IntersectionObserver) so it always runs AFTER the
    // bitmap exists, even with native lazy loading.
    const kick = () => {
      cell.style.transform = 'translateZ(0.0001px)';
      void cell.offsetHeight;
      requestAnimationFrame(() => { cell.style.transform = ''; });
    };
    img.addEventListener('load', () => {
      if (img.decode) img.decode().then(kick).catch(kick);
      else kick();
    });

    cell.appendChild(img);
    img.src = src;
    wrap.appendChild(cell);
  }

  return wrap;
}

// Click-to-zoom: tags an image as expandable and lazily mounts a
// single shared overlay on first open. Click anywhere on the
// overlay (or hit Escape) to close.
let zoomOverlay = null;
function ensureZoomOverlay() {
  if (zoomOverlay) return zoomOverlay;
  zoomOverlay = document.createElement('div');
  zoomOverlay.className = 'zoom-overlay';
  zoomOverlay.setAttribute('role', 'dialog');
  zoomOverlay.setAttribute('aria-hidden', 'true');
  const big = document.createElement('img');
  big.alt = '';
  zoomOverlay.appendChild(big);
  // Ignore the close-click while the overlay is still opening —
  // iOS Safari dispatches the originating tap to whatever element
  // is under the finger at touchend time, which is the overlay
  // itself once it has popped in front of the page.
  zoomOverlay.addEventListener('click', () => {
    if (zoomOverlay.classList.contains('opening')) return;
    closeZoom();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeZoom();
  });
  document.body.appendChild(zoomOverlay);
  return zoomOverlay;
}
function openZoom(src) {
  const ov = ensureZoomOverlay();
  ov.querySelector('img').src = src;
  ov.classList.add('open', 'opening');
  ov.setAttribute('aria-hidden', 'false');
  document.documentElement.style.overflow = 'hidden';
  setTimeout(() => ov.classList.remove('opening'), 350);
}
function closeZoom() {
  if (!zoomOverlay) return;
  zoomOverlay.classList.remove('open');
  zoomOverlay.setAttribute('aria-hidden', 'true');
  document.documentElement.style.overflow = '';
}
function makeZoomable(img) {
  img.classList.add('zoomable');
  img.addEventListener('click', () => {
    const src = img.currentSrc || img.src || img.dataset.src;
    if (src) openZoom(src);
  });
}

function videoBlock(b) {
  const wrap = document.createElement('figure');
  wrap.className = `block block-video ${b.layout || 'center'}`;
  if (b.srcMobile) wrap.classList.add('has-mobile-src');
  const v = document.createElement('video');
  v.muted = b.muted ?? true;
  v.loop = b.loop ?? true;
  v.playsInline = true;
  v.autoplay = b.autoplay ?? true;
  v.controls = b.controls ?? false;
  if (b.poster) v.poster = b.poster;
  v.preload = 'metadata';

  // When `srcMobile` is set, swap the source based on viewport. Used
  // for clips that were shot in different orientations for desktop
  // vs. phone (e.g. a landscape hero that becomes a portrait crop on
  // narrow screens). The currentTime is preserved across the swap so
  // a partly-played clip doesn't jump back to zero.
  if (b.srcMobile) {
    const mq = window.matchMedia('(max-width: 640px)');
    const pickSrc = () => (mq.matches ? b.srcMobile : b.src);
    v.src = pickSrc();
    const handle = () => {
      const t = v.currentTime;
      const wasPlaying = !v.paused;
      v.src = pickSrc();
      v.addEventListener('loadedmetadata', () => {
        try { v.currentTime = t; } catch {}
        if (wasPlaying) v.play().catch(() => {});
      }, { once: true });
    };
    mq.addEventListener('change', handle);
  } else {
    v.src = b.src;
  }

  // Pause when off-screen so the GPU isn't decoding for nothing.
  const io = new IntersectionObserver((entries) => {
    for (const e of entries) {
      if (e.isIntersecting) v.play().catch(() => {});
      else v.pause();
    }
  }, { threshold: 0.1 });
  io.observe(v);

  wrap.appendChild(v);
  if (b.caption) {
    const cap = document.createElement('figcaption');
    cap.className = 'block-caption';
    cap.textContent = b.caption;
    wrap.appendChild(cap);
  }
  return wrap;
}

function modelBlock(b) {
  // Two layout modes:
  //   default  — fixed-aspect viewer, drag to orbit
  //   sticky   — viewer pins for ~2.5 viewport heights of scroll, rotation tied to scroll
  if (b.scrubScroll) {
    const wrap = document.createElement('div');
    wrap.className = 'block block-model sticky-wrap';
    const track = document.createElement('div');
    track.className = 'scroll-track';
    const inner = document.createElement('div');
    inner.className = 'sticky-inner';
    track.appendChild(inner);
    wrap.appendChild(track);

    queueViewerMount(inner, b, () => sectionScrollProgress(track));
    return wrap;
  }

  const wrap = document.createElement('div');
  wrap.className = `block block-model ${b.layout || 'center'}`;
  queueViewerMount(wrap, b);
  return wrap;
}

// Multiple 3D models side-by-side. Each entry inherits the block's
// defaults (background, autorotate, …) but can override per-model —
// useful for showing a family of objects in different tints.
function modelGridBlock(b) {
  const wrap = document.createElement('div');
  wrap.className = 'block block-model-grid';
  wrap.style.setProperty('--cols', String(b.cols || 3));
  wrap.style.setProperty('--cols-mobile', String(b.colsMobile || 1));
  if (b.aspectRatio) wrap.style.setProperty('--model-aspect', b.aspectRatio);
  // Defaults that get merged into each item — drop the keys that
  // shouldn't carry over (e.g. the `models` array itself).
  const { models, cols, colsMobile, aspectRatio, type, ...defaults } = b;
  for (const item of models || []) {
    const cell = document.createElement('div');
    cell.className = 'model-cell';
    const itemBlock = { ...defaults, ...item };
    queueViewerMount(cell, itemBlock);
    wrap.appendChild(cell);
  }
  return wrap;
}

function queueViewerMount(container, block, getProgress) {
  // Defer until container is on-screen so we don't pay for Three.js
  // up front for users who never scroll to a 3D project.
  const io = new IntersectionObserver(
    (entries, obs) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          obs.disconnect();
          mountModelViewer(container, block, getProgress).catch((err) =>
            console.error('[render] mount model failed', err)
          );
        }
      }
    },
    { rootMargin: '200px 0px' }
  );
  io.observe(container);
}

function modelSequenceBlock(b) {
  if (b.scrubScroll) {
    // Scroll-locked mode: a single 100vh section. While the section
    // is centered in the viewport, wheel/touch/keys are captured and
    // drive `uProgress` directly — the page itself stops scrolling
    // until the animation completes.
    const wrap = document.createElement('div');
    wrap.className = 'block block-model lock-wrap';
    queueSequenceMount(wrap, b);
    return wrap;
  }

  const wrap = document.createElement('div');
  wrap.className = `block block-model ${b.layout || 'center'}`;
  queueSequenceMount(wrap, b);
  return wrap;
}

function queueSequenceMount(container, block, getProgress) {
  const io = new IntersectionObserver(
    (entries, obs) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          obs.disconnect();
          mountModelSequence(container, block, getProgress).catch((err) =>
            console.error('[render] mount sequence failed', err)
          );
        }
      }
    },
    { rootMargin: '200px 0px' }
  );
  io.observe(container);
}

function sectionScrollProgress(el) {
  const rect = el.getBoundingClientRect();
  const total = rect.height - window.innerHeight;
  if (total <= 0) return 0;
  const scrolled = -rect.top;
  return Math.min(1, Math.max(0, scrolled / total));
}

// Instagram embed via iframe — Instagram hosts the video and
// thumbnail, so the project's own assets stay small. The block
// accepts either a full post URL ("https://www.instagram.com/p/XYZ/"
// or "/reel/XYZ/") or just the bare shortcode.
function instagramBlock(b) {
  const wrap = document.createElement('figure');
  wrap.className = `block block-instagram ${b.layout || 'center'}`;
  const url = b.url || b.permalink || '';
  const shortcodeMatch = url.match(/\/(p|reel|tv)\/([^/?#]+)/);
  const shortcode = shortcodeMatch ? shortcodeMatch[2] : '';
  // Optional `img_index` selects the starting carousel item.
  const idx = url.match(/[?&]img_index=(\d+)/)?.[1];
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.instagram.com/p/${shortcode}/embed/${idx ? `?img_index=${idx}` : ''}`;
  iframe.allow = 'autoplay; clipboard-write; encrypted-media; picture-in-picture';
  iframe.allowFullscreen = true;
  iframe.loading = 'lazy';
  wrap.appendChild(iframe);
  if (b.caption) {
    const cap = document.createElement('figcaption');
    cap.className = 'block-caption';
    cap.textContent = b.caption;
    wrap.appendChild(cap);
  }
  return wrap;
}

// Mixed-media grid: each item is either a video (.mp4/.webm/.mov)
// or an image (.jpg/.png/.webp/.avif). Detection by file extension.
// Videos autoplay muted-loop; images are click-to-zoom when the
// block has `zoomable: true`. Layout matches video-grid/image-grid.
function mediaGridBlock(b) {
  const wrap = document.createElement('div');
  wrap.className = 'block block-media-grid';
  wrap.style.setProperty('--cols', String(b.cols || 3));
  wrap.style.setProperty('--cols-mobile', String(b.colsMobile || 1));
  if (b.aspectRatio) wrap.style.setProperty('--media-aspect', b.aspectRatio);
  const items = b.items || [];
  const videos = [];
  for (const item of items) {
    const src = typeof item === 'string' ? item : item.src;
    const isVideo = /\.(mp4|webm|mov)(\?|$)/i.test(src);
    const cell = document.createElement('div');
    cell.className = 'media-cell';
    if (isVideo) {
      const v = document.createElement('video');
      v.src = src;
      v.muted = true;
      v.loop = true;
      v.playsInline = true;
      v.autoplay = true;
      v.controls = false;
      v.preload = 'metadata';
      cell.appendChild(v);
      videos.push(v);
    } else {
      const img = document.createElement('img');
      img.src = src;
      img.alt = (typeof item === 'string' ? '' : item.alt) || '';
      img.decoding = 'async';
      if (b.zoomable) makeZoomable(img);
      cell.appendChild(img);
    }
    wrap.appendChild(cell);
  }
  // Pause off-screen videos like in video-grid.
  if (videos.length) {
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        const el = e.target;
        if (e.isIntersecting) el.play().catch(() => {});
        else el.pause();
      }
    }, { threshold: 0.1 });
    for (const v of videos) io.observe(v);
  }
  return wrap;
}

function videoGridBlock(b) {
  const wrap = document.createElement('div');
  wrap.className = 'block block-video-grid';
  wrap.style.setProperty('--cols', String(b.cols || 3));
  wrap.style.setProperty('--cols-mobile', String(b.colsMobile || 1));
  if (b.aspectRatio) wrap.style.setProperty('--video-aspect', b.aspectRatio);
  // `fitHeight: true` arranges the tiles in a flex row sized by
  // their height (max 80vh) instead of filling page width — useful
  // for portrait clips that would sprawl on a desktop monitor.
  if (b.fitHeight) wrap.classList.add('fit-height');
  const items = b.videos || [];
  const videos = [];
  for (const item of items) {
    const src = typeof item === 'string' ? item : item.src;
    const poster = typeof item === 'string' ? null : item.poster;
    const v = document.createElement('video');
    v.src = src;
    if (poster) v.poster = poster;
    v.muted = true;
    v.loop = true;
    v.playsInline = true;
    v.autoplay = true;
    v.controls = false;
    v.preload = 'metadata';
    wrap.appendChild(v);
    videos.push(v);
  }
  // Only decode/play tiles that are on-screen — eight muted reels
  // running off-screen wastes GPU and battery.
  const io = new IntersectionObserver((entries) => {
    for (const e of entries) {
      const el = e.target;
      if (e.isIntersecting) el.play().catch(() => {});
      else el.pause();
    }
  }, { threshold: 0.1 });
  for (const v of videos) io.observe(v);
  return wrap;
}

// External-link block — pointer to another site (gallery,
// collaborator, etc.). Renders as a single bordered row with the
// label on the left and the destination host on the right.
function linkBlock(b) {
  const wrap = document.createElement('div');
  wrap.className = 'block block-link';
  if (b.heading) {
    const h = document.createElement('h3');
    h.textContent = b.heading;
    wrap.appendChild(h);
  }
  const items = b.items || (b.href ? [{ href: b.href, label: b.label, meta: b.meta }] : []);
  for (const item of items) {
    const a = document.createElement('a');
    a.className = 'external-link';
    a.href = item.href;
    a.target = '_blank';
    a.rel = 'noopener';
    const text = document.createElement('span');
    text.className = 'external-link-label';
    text.textContent = item.label || item.href;
    const meta = document.createElement('span');
    meta.className = 'external-link-meta';
    let host = item.meta;
    if (!host) {
      try { host = new URL(item.href).host.replace(/^www\./, ''); } catch {}
    }
    meta.textContent = `${host || ''} · Open →`;
    a.append(text, meta);
    wrap.appendChild(a);
  }
  return wrap;
}

// YouTube embed. Accepts a full URL (`watch?v=…`, `youtu.be/…`,
// or `embed/…`) or a bare video id via `b.id`.
function youtubeBlock(b) {
  const wrap = document.createElement('figure');
  wrap.className = `block block-youtube ${b.layout || 'center'}`;
  let id = b.id || '';
  if (!id && b.url) {
    const m = String(b.url).match(/(?:embed\/|v=|youtu\.be\/)([\w-]+)/);
    id = m ? m[1] : '';
  }
  if (id) {
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.youtube.com/embed/${id}`;
    iframe.allow = 'autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share';
    iframe.allowFullscreen = true;
    iframe.loading = 'lazy';
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    wrap.appendChild(iframe);
  }
  if (b.caption) {
    const cap = document.createElement('figcaption');
    cap.className = 'block-caption';
    cap.textContent = b.caption;
    wrap.appendChild(cap);
  }
  return wrap;
}

function instagramGridBlock(b) {
  const wrap = document.createElement('div');
  wrap.className = 'block block-instagram-grid';
  wrap.style.setProperty('--cols', String(b.cols || 3));
  wrap.style.setProperty('--cols-mobile', String(b.colsMobile || 1));
  for (const url of b.urls || []) {
    const s = String(url).replace(/[?#].*$/, '').replace(/\/$/, '');
    // Preserve the original path kind (`/p/`, `/reel/`, `/tv/`) so
    // reels embed as reels. Falls back to `/p/` for a bare
    // shortcode.
    const kindMatch = s.match(/\/(p|reel|tv)\/([^/]+)/);
    const embedSrc = kindMatch
      ? `https://www.instagram.com/${kindMatch[1]}/${kindMatch[2]}/embed/`
      : `https://www.instagram.com/p/${s.split('/').pop()}/embed/`;
    const iframe = document.createElement('iframe');
    iframe.src = embedSrc;
    iframe.allow = 'autoplay; clipboard-write; encrypted-media; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.loading = 'lazy';
    iframe.scrolling = 'no';
    wrap.appendChild(iframe);
  }
  return wrap;
}

function downloadsBlock(b) {
  const wrap = document.createElement('div');
  wrap.className = 'block block-downloads';
  if (b.heading) {
    const h = document.createElement('h3');
    h.textContent = b.heading;
    wrap.appendChild(h);
  }
  for (const item of b.items || []) {
    const a = document.createElement('a');
    a.className = 'download';
    a.href = item.src;
    a.target = '_blank';
    a.rel = 'noopener';
    if (item.download !== false) a.download = '';
    if (item.thumb) {
      const thumb = document.createElement('img');
      thumb.className = 'download-thumb';
      thumb.src = item.thumb;
      thumb.alt = '';
      thumb.loading = 'lazy';
      thumb.decoding = 'async';
      a.appendChild(thumb);
      a.classList.add('has-thumb');
    }
    const text = document.createElement('span');
    text.className = 'download-text';
    const label = document.createElement('span');
    label.className = 'download-label';
    label.textContent = item.label || item.src.split('/').pop();
    const meta = document.createElement('span');
    meta.className = 'download-meta';
    const parts = [];
    if (item.type) parts.push(item.type);
    if (item.size) parts.push(item.size);
    parts.push('Download →');
    meta.textContent = parts.join(' · ');
    text.append(label, meta);
    a.appendChild(text);
    wrap.appendChild(a);
  }
  return wrap;
}

function spacerBlock(b) {
  const el = document.createElement('div');
  el.className = 'block';
  el.style.height = `${b.size || 80}px`;
  el.style.opacity = 1;
  el.style.transform = 'none';
  return el;
}

function renderFooter(site) {
  const f = document.createElement('footer');
  f.className = 'site-footer';

  const about = document.createElement('div');
  about.innerHTML = `<h3>${escape(site.shortName || site.name || '')}</h3>` +
    `<div>${escape(site.footer || '')}</div>`;

  const contact = document.createElement('div');
  contact.innerHTML = `<h3>Contact</h3>`;
  for (const c of site.contact || []) {
    const a = document.createElement('a');
    a.href = c.href;
    a.textContent = c.label;
    if (/^https?:/.test(c.href)) { a.target = '_blank'; a.rel = 'noopener'; }
    contact.appendChild(a);
  }

  let elsewhere = null;
  if (Array.isArray(site.elsewhere) && site.elsewhere.length > 0) {
    elsewhere = document.createElement('div');
    elsewhere.innerHTML = `<h3>Elsewhere</h3>`;
    for (const c of site.elsewhere) {
      const a = document.createElement('a');
      a.href = c.href; a.target = '_blank'; a.rel = 'noopener';
      a.textContent = c.label;
      elsewhere.appendChild(a);
    }
  }

  const colophon = document.createElement('div');
  colophon.className = 'colophon';
  const year = new Date().getFullYear();
  colophon.textContent = `© ${year} ${site.name || ''}. Set in Inter.`;

  if (elsewhere) f.append(about, contact, elsewhere, colophon);
  else f.append(about, contact, colophon);
  return f;
}

function escape(s) {
  return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}
